/*
 * Free Gift Campaign
 * ------------------
 * Automatically adds a configured gift product to the cart once the cart value
 * (after discounts, excluding the gift itself) reaches the market threshold, and
 * removes it again if the cart drops below the threshold.
 *
 * GLOBAL detection: the cart can be changed from many places that do NOT fire the
 * theme's `cartUpdate` pub/sub event — drawer upsells, third-party apps with their
 * own add-to-cart JS, custom sections, etc. To catch ALL of them, this script
 * intercepts every network call to the Shopify cart AJAX endpoints (fetch + XHR)
 * and reconciles the gift after each one. It also reconciles when the cart drawer
 * opens and on the theme's own cartUpdate event, as extra safety nets.
 *
 * Loop safety: this script's OWN add/remove requests go through the ORIGINAL
 * (un-intercepted) fetch, so they never re-trigger the interceptor.
 *
 * Config is injected server-side as `window.freeGiftConfig` (see snippets/cart-drawer.liquid):
 *   { enabled, removeExistingGift, currency, thresholdCents, giftVariantId, giftAvailable }
 *
 * Rules enforced here:
 *   - Only ever one free gift line (identified STRICTLY by the `_free_gift`
 *     line property). A manual purchase of the same product creates a separate
 *     line without this property and is NEVER touched by this script — customers
 *     can freely buy the gift product at full price alongside the freebie.
 *   - Quantity of the gift line is clamped to 1 (customers cannot increase it).
 *   - The gift is not added if it is out of stock.
 *   - Threshold is measured on cart.total_price (post-discount) minus the gift line.
 *
 * Speed: mutations request `sections` + `sections_url` inline (same pattern as
 * cart.js `updateQuantity`), so the response returns pre-rendered HTML for the
 * cart drawer / cart page / bubble in the SAME round trip — no follow-up
 * `?section_id=` fetches per section.
 *
 * Making the gift actually free is handled by a matching Shopify automatic
 * "minimum purchase" discount in Admin — this script only manages presence/quantity.
 */
(function () {
  if (window.__freeGiftInit) return;
  window.__freeGiftInit = true;

  const cfg = window.freeGiftConfig;

  // Capture pristine network primitives BEFORE anything (incl. our interceptor)
  // wraps them, so our own gift calls bypass the interceptor and never loop.
  const nativeFetch = window.fetch ? window.fetch.bind(window) : null;

  const routes = window.routes || {};
  const CART_URL = routes.cart_url || '/cart';
  const ADD_URL = routes.cart_add_url || '/cart/add';
  const CHANGE_URL = routes.cart_change_url || '/cart/change';

  const GIFT_PROP = '_free_gift';
  const MUTATION_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/i;

  let busy = false;
  let dirty = false;
  let reconcileTimer = null;

  // ── Network interception (global catch-all) ─────────────────────────────────
  function urlOf(input) {
    if (typeof input === 'string') return input;
    if (input && typeof input.url === 'string') return input.url; // Request object
    return '';
  }

  function isCartMutation(url) {
    return MUTATION_RE.test(url || '');
  }

  function installInterceptors() {
    // fetch
    if (nativeFetch) {
      window.fetch = function (input, init) {
        const mutating = isCartMutation(urlOf(input));
        const result = nativeFetch(input, init);
        if (mutating && result && typeof result.then === 'function') {
          result.then(() => scheduleReconcile()).catch(() => {});
        }
        return result;
      };
    }

    // XMLHttpRequest (jQuery.ajax and many apps use this)
    const proto = window.XMLHttpRequest && window.XMLHttpRequest.prototype;
    if (proto && !proto.__fgPatched) {
      proto.__fgPatched = true;
      const nativeOpen = proto.open;
      const nativeSend = proto.send;
      proto.open = function (method, url) {
        try {
          this.__fgMutation = isCartMutation(url);
        } catch (e) {
          this.__fgMutation = false;
        }
        return nativeOpen.apply(this, arguments);
      };
      proto.send = function () {
        if (this.__fgMutation) {
          this.addEventListener('loadend', () => scheduleReconcile());
        }
        return nativeSend.apply(this, arguments);
      };
    }
  }

  // ── Gift reconciliation ─────────────────────────────────────────────────────
  function getCart() {
    return nativeFetch(`${CART_URL}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
  }

  function findGiftLine(cart) {
    // STRICT: only lines carrying our `_free_gift` property are considered ours.
    // A manually-added line with the same variant is a normal paid purchase and
    // must be left untouched — no variant_id fallback here on purpose.
    return cart.items.find((it) => it.properties && it.properties[GIFT_PROP]) || null;
  }

  function eligibleCents(cart, giftLine) {
    let cents = cart.total_price;
    if (giftLine) cents -= giftLine.final_line_price;
    return cents;
  }

  // Only ask Shopify to render sections that actually exist on this page. Same
  // list `syncCartUI` used to fetch one-by-one — now bundled into the mutation.
  function requestedSectionIds() {
    const ids = [];
    if (document.querySelector('cart-drawer')) ids.push('cart-drawer');
    if (document.getElementById('main-cart-items')) ids.push('main-cart-items');
    if (document.getElementById('main-cart-footer')) ids.push('main-cart-footer');
    if (document.getElementById('cart-icon-bubble')) ids.push('cart-icon-bubble');
    return ids;
  }

  function addGift() {
    return nativeFetch(`${ADD_URL}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(cfg.giftVariantId), quantity: 1, properties: { [GIFT_PROP]: 'true' } }],
        sections: requestedSectionIds().join(','),
        sections_url: window.location.pathname,
      }),
    }).then((r) => (r.ok ? r.json() : null));
  }

  function changeGift(giftLine, quantity) {
    return nativeFetch(`${CHANGE_URL}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id: giftLine.key,
        quantity: quantity,
        sections: requestedSectionIds().join(','),
        sections_url: window.location.pathname,
      }),
    }).then((r) => (r.ok ? r.json() : null));
  }

  // Full re-render of the visible cart UI (drawer + cart page + bubble), including
  // the empty state, using pre-rendered HTML that came back with the mutation.
  // We deep-replace `.drawer__inner` (not just `cart-drawer-items`) so the
  // empty-state markup swaps in when our gift removal empties the cart —
  // matches what cart.js does on a normal remove.
  function applySections(sectionsMap, itemCount) {
    if (!sectionsMap) return;
    const empty = itemCount === 0;
    const parse = (html) => (html ? new DOMParser().parseFromString(html, 'text/html') : null);

    const drawerDoc = parse(sectionsMap['cart-drawer']);
    if (drawerDoc) {
      const src = drawerDoc.querySelector('.drawer__inner');
      const tgt = document.querySelector('#CartDrawer .drawer__inner');
      if (src && tgt) tgt.innerHTML = src.innerHTML;
      const drawer = document.querySelector('cart-drawer');
      if (drawer) drawer.classList.toggle('is-empty', empty);
    }

    const itemsDoc = parse(sectionsMap['main-cart-items']);
    if (itemsDoc) {
      const src = itemsDoc.querySelector('#main-cart-items .js-contents');
      const tgt = document.querySelector('#main-cart-items .js-contents');
      if (src && tgt) tgt.innerHTML = src.innerHTML;
      const ci = document.querySelector('cart-items');
      if (ci) ci.classList.toggle('is-empty', empty);
    }

    const footerDoc = parse(sectionsMap['main-cart-footer']);
    if (footerDoc) {
      const src = footerDoc.querySelector('#main-cart-footer .js-contents');
      const tgt = document.querySelector('#main-cart-footer .js-contents');
      if (src && tgt) tgt.innerHTML = src.innerHTML;
      const f = document.getElementById('main-cart-footer');
      if (f) f.classList.toggle('is-empty', empty);
    }

    const bubbleDoc = parse(sectionsMap['cart-icon-bubble']);
    if (bubbleDoc) {
      const src = bubbleDoc.querySelector('.shopify-section') || bubbleDoc.body;
      const tgt = document.getElementById('cart-icon-bubble');
      if (src && tgt) tgt.innerHTML = src.innerHTML;
    }
  }

  function scheduleReconcile() {
    clearTimeout(reconcileTimer);
    reconcileTimer = setTimeout(reconcile, 80);
  }

  function reconcile() {
    if (busy) {
      dirty = true;
      return;
    }
    busy = true;

    getCart()
      .then((cart) => {
        const giftLine = findGiftLine(cart);
        const value = eligibleCents(cart, giftLine);
        const canAddGift = Boolean(cfg.enabled && cfg.giftVariantId);
        const qualifies = canAddGift && cart.item_count > 0 && value >= cfg.thresholdCents;

        if (qualifies && !giftLine) {
          if (!cfg.giftAvailable) return null;
          // /cart/add.js response omits `item_count`; we just added a line so
          // the cart is definitely non-empty — pass 1 for the empty toggle.
          return addGift().then((res) => {
            if (res) applySections(res.sections, 1);
            return null;
          });
        }
        if (!qualifies && giftLine) {
          return changeGift(giftLine, 0).then((res) => {
            if (res) applySections(res.sections, res.item_count || 0);
            return null;
          });
        }
        if (giftLine && giftLine.quantity > 1) {
          // Enforce a single free gift — customers cannot increase the quantity.
          return changeGift(giftLine, 1).then((res) => {
            if (res) applySections(res.sections, res.item_count || 0);
            return null;
          });
        }
        return null;
      })
      .catch((e) => {
        console.error('[free-gift]', e);
      })
      .then(() => {
        busy = false;
        if (dirty) {
          dirty = false;
          reconcile();
        }
      });
  }

  // ── Extra safety nets ───────────────────────────────────────────────────────
  function observeDrawer() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || drawer.__fgObserved) return;
    drawer.__fgObserved = true;
    let wasActive = drawer.classList.contains('active');
    const obs = new MutationObserver(() => {
      const isActive = drawer.classList.contains('active');
      // Only reconcile when the drawer is opened (transition to active), not on every
      // class change (e.g. our own is-empty toggle) while it is already open.
      if (isActive && !wasActive) scheduleReconcile();
      wasActive = isActive;
    });
    obs.observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }

  function boot() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event && event.source === 'free-gift') return; // ignore our own refresh
        scheduleReconcile();
      });
    }
    observeDrawer();
    reconcile(); // initial sync on page load
  }

  // Interceptors must be installed for active campaigns and for remove-only mode
  // after a market is disabled, so existing gift lines can be cleaned up.
  if (!cfg || !nativeFetch) return;
  if (!cfg.enabled && !cfg.removeExistingGift) return;
  if (cfg.enabled && !cfg.giftVariantId) return;

  installInterceptors();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
